import {
  AudioWorkletRecorder,
  ChunkUploadQueue,
  openMic,
  RecordingDB,
  stopStream,
  type MicChoice,
} from '@clarix/meeting-recording';
import type { S3PartRecord, UploadPartInfo } from '@clarix/shared-types';
import { format } from 'date-fns';
import { quickMeetingApi } from './api';
import { micStorageKey } from './constants';

// Browser primitives for the recorder: mic probe, chime, Web Lock, local storage, IndexedDB drain.

export type MicProbe = 'granted' | 'denied' | 'no_device' | 'unsupported' | 'error';

/** A v4 uuid; `randomUUID` exists only in secure contexts and the shell also runs on plain-http hosts. */
export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Opens the mic the recording will use and releases it at once, so a refusal never reaches the
 * server (§18.4). Same `openMic` as the engine, so the probe can't succeed on a different device.
 */
export async function probeMicrophone(saved: MicChoice | null): Promise<MicProbe> {
  const canRecord =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    (AudioWorkletRecorder.isSupported() || typeof MediaRecorder !== 'undefined');
  if (!canRecord) return 'unsupported';
  try {
    const perm = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
    if (perm?.state === 'denied') return 'denied';
  } catch {
    /* 'microphone' is not a queryable name in every browser */
  }
  try {
    const { stream } = await openMic(saved, { fallback: true });
    stopStream(stream);
    return 'granted';
  } catch (err) {
    const name = err instanceof DOMException ? err.name : '';
    if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
    if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no_device';
    return 'error';
  }
}

let chimeContext: AudioContext | null = null;

/** Create/resume the chime's AudioContext inside the click, before any await (autoplay policy). */
export function primeChime(): void {
  try {
    chimeContext ??= new AudioContext();
    if (chimeContext.state === 'suspended') void chimeContext.resume();
  } catch {
    chimeContext = null;
  }
}

const CHIME_STEP_S = 0.16;
const CHIME_TONE_S = 0.15;
/** Room echo after the last tone: capture must not open inside it. */
const CHIME_TAIL_MS = 250;

/**
 * The audible start/stop cue that makes the recording perceptible to the room (§6.2). Resolves
 * once it has left the speakers, so the start cue can finish before capture opens.
 */
export function playChime(kind: 'start' | 'stop'): Promise<void> {
  primeChime();
  const ctx = chimeContext;
  if (!ctx) return Promise.resolve();
  const tones = kind === 'start' ? [660, 880] : [880, 660];
  const t0 = ctx.currentTime + 0.02;
  tones.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const at = t0 + i * CHIME_STEP_S;
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.14);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + CHIME_TONE_S);
  });
  const endsInS = t0 - ctx.currentTime + (tones.length - 1) * CHIME_STEP_S + CHIME_TONE_S;
  const latencyS = ctx.outputLatency || ctx.baseLatency || 0;
  // A timer, not `onended`: a suspended context never ends its oscillators.
  return new Promise((resolve) => window.setTimeout(resolve, (endsInS + latencyS) * 1000 + CHIME_TAIL_MS));
}

/**
 * Holds the cross-tab Web Lock for the whole recording. Resolves to its release function, or
 * null when another tab holds it. Without Web Locks it resolves a no-op (the server's 409 stays).
 */
export function acquireRecordingLock(name: string): Promise<(() => void) | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return Promise.resolve(() => {});
  return new Promise((resolve) => {
    locks
      .request(name, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(null);
          return undefined;
        }
        return new Promise<void>((release) => resolve(() => release()));
      })
      .catch(() => resolve(null));
  });
}

/** Whether some tab of this origin holds the lock; null when the browser can't tell. */
export async function isRecordingLockHeld(name: string): Promise<boolean | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks?.query) return null;
  try {
    const snapshot = await locks.query();
    return (snapshot.held ?? []).some((l) => l.name === name);
  } catch {
    return null;
  }
}

// ── localStorage (per-viewer conveniences; every access may throw) ──────────────────────

/**
 * What a later Retry needs to drain this recording's IndexedDB parts. It is the only index to those
 * parts, so it is removed only once they are gone; a take that ended with audio left is `ended`.
 */
export interface SessionSidecar {
  recordingId: string;
  clientSessionId: string;
  uploadId: string;
  title?: string;
  startedAt?: string;
  ended?: boolean;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked */
  }
}

export const sidecars = {
  list(scope: string): SessionSidecar[] {
    const list = readJson<unknown>(`${scope}:sessions`, []);
    return Array.isArray(list) ? (list as SessionSidecar[]) : [];
  },
  find(scope: string, recordingId: string): SessionSidecar | undefined {
    return this.list(scope).find((s) => s.recordingId === recordingId);
  },
  add(scope: string, entry: SessionSidecar): void {
    const rest = this.list(scope).filter((s) => s.recordingId !== entry.recordingId);
    writeJson(`${scope}:sessions`, [entry, ...rest]);
  },
  update(scope: string, recordingId: string, patch: Partial<SessionSidecar>): void {
    writeJson(
      `${scope}:sessions`,
      this.list(scope).map((s) => (s.recordingId === recordingId ? { ...s, ...patch } : s)),
    );
  },
  /** The take is over but its audio is still on this device: kept for "Download from this device". */
  markEnded(scope: string, recordingId: string): void {
    this.update(scope, recordingId, { ended: true });
  },
  remove(scope: string, recordingId: string): void {
    writeJson(
      `${scope}:sessions`,
      this.list(scope).filter((s) => s.recordingId !== recordingId),
    );
  },
};

const micListeners = new Set<() => void>();

function parseMicChoice(raw: string | null): MicChoice | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { deviceId?: unknown; label?: unknown } | null;
    if (typeof v?.deviceId !== 'string' || !v.deviceId) return null;
    return typeof v.label === 'string' && v.label ? { deviceId: v.deviceId, label: v.label } : { deviceId: v.deviceId };
  } catch {
    return null;
  }
}

/** The user's chosen mic; nothing stored means "System default". */
export const savedMic = {
  /** The stored string, so `useSyncExternalStore` gets a stable snapshot. */
  raw(userId: string | null): string | null {
    if (!userId) return null;
    try {
      return localStorage.getItem(micStorageKey(userId));
    } catch {
      return null;
    }
  },
  parse: parseMicChoice,
  get(userId: string | null): MicChoice | null {
    return parseMicChoice(savedMic.raw(userId));
  },
  set(userId: string | null, choice: MicChoice | null): void {
    if (!userId) return;
    try {
      if (choice) localStorage.setItem(micStorageKey(userId), JSON.stringify({ deviceId: choice.deviceId, label: choice.label }));
      else localStorage.removeItem(micStorageKey(userId));
    } catch {
      /* storage full or blocked: the choice lasts until reload */
    }
    micListeners.forEach((l) => l());
  },
  subscribe(onChange: () => void): () => void {
    micListeners.add(onChange);
    window.addEventListener('storage', onChange);
    return () => {
      micListeners.delete(onChange);
      window.removeEventListener('storage', onChange);
    };
  },
};

// ── IndexedDB drain (the engine has no resume API, §18.4) ───────────────────────────────

/** Uploads the parts the worklet left in IndexedDB and returns every uploaded part. */
export async function drainLocalParts(
  dbName: string,
  slug: string,
  entry: SessionSidecar,
): Promise<UploadPartInfo[]> {
  const db = new RecordingDB({ dbName });
  await db.open();
  try {
    const pending = await db.getPendingParts(entry.clientSessionId);
    if (pending.length > 0) {
      const queue = new ChunkUploadQueue(db, entry.clientSessionId, entry.uploadId, (payload) =>
        quickMeetingApi.uploadUrls(slug, entry.recordingId, payload),
      );
      await queue.drain();
    }
    return await db.getCompletedParts(entry.clientSessionId);
  } finally {
    db.close();
  }
}

/** The server lacks these uploaded parts (all when empty): mark them for upload again. */
export async function requeueLocalParts(dbName: string, clientSessionId: string, partNumbers: readonly number[]): Promise<number> {
  const db = new RecordingDB({ dbName });
  await db.open();
  try {
    return await db.requeueParts(clientSessionId, partNumbers);
  } finally {
    db.close();
  }
}

/** The server re-opened a lost upload: every local part with audio is sent again under the new one. */
export async function restartLocalUpload(dbName: string, clientSessionId: string): Promise<number> {
  const db = new RecordingDB({ dbName });
  await db.open();
  try {
    return await db.restartUpload(clientSessionId);
  } finally {
    db.close();
  }
}

/** One take's audio from its IndexedDB records, in part order; zero-byte markers carry none. */
export function assembleLocalAudio(records: readonly S3PartRecord[]): Blob | null {
  const blobs = records.map((r) => r.blob).filter((b) => b.size > 0);
  return blobs.length ? new Blob(blobs, { type: blobs[0]!.type || 'audio/mp4' }) : null;
}

/** This take's audio still on the device, as one file; null when nothing is left. */
export async function readLocalAudio(dbName: string, clientSessionId: string): Promise<Blob | null> {
  const db = new RecordingDB({ dbName });
  try {
    await db.open();
    return assembleLocalAudio(await db.getParts(clientSessionId));
  } catch {
    return null;
  } finally {
    db.close();
  }
}

/** Takes that ended with audio left on this device; entries whose audio is gone are dropped. */
export async function pruneLocalCopies(scope: string): Promise<SessionSidecar[]> {
  const kept: SessionSidecar[] = [];
  for (const entry of sidecars.list(scope).filter((s) => s.ended)) {
    if (await readLocalAudio(scope, entry.clientSessionId)) kept.push(entry);
    else sidecars.remove(scope, entry.recordingId);
  }
  return kept;
}

const AUDIO_EXTENSION: Record<string, string> = { 'audio/mp4': 'm4a', 'audio/webm': 'webm', 'audio/ogg': 'ogg' };

/** "quick-meeting-2026-10-16-1430.m4a", named like the server's own downloads. */
export function localAudioFileName(entry: Pick<SessionSidecar, 'startedAt'>, mimeType: string): string {
  const when = entry.startedAt ? new Date(entry.startedAt) : new Date();
  const ext = AUDIO_EXTENSION[mimeType.split(';')[0]!.trim()] ?? 'm4a';
  return `quick-meeting-${format(when, 'yyyy-MM-dd-HHmm')}.${ext}`;
}

/** Saves this take's on-device audio as a file; false when there is nothing to save. */
export async function downloadLocalAudio(dbName: string, entry: SessionSidecar): Promise<boolean> {
  const blob = await readLocalAudio(dbName, entry.clientSessionId);
  if (!blob) return false;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = localAudioFileName(entry, blob.type);
  link.click();
  // The browser reads the URL after this click returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}

/** Drops this recording's local parts, after a save or a "delete from this device". */
export async function clearLocalParts(dbName: string, clientSessionId: string): Promise<void> {
  const db = new RecordingDB({ dbName });
  try {
    await db.open();
    await db.clearRecording(clientSessionId);
  } catch {
    /* best effort */
  } finally {
    db.close();
  }
}
